import React, {
  useEffect,
  useRef
} from 'react';

import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const MercadoLibreAccountSync = () => {
  const { user } = useAuth();

  const runningRef = useRef(false);

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    let stopped = false;

    const apiBaseUrl =
      import.meta.env?.VITE_API_BASE_URL ||
      window.location.origin;

    const getAppSessionToken = async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      return session?.access_token || null;
    };

    const syncMercadoLibreAccount = async () => {
      if (
        stopped ||
        runningRef.current
      ) {
        return;
      }

      runningRef.current = true;

      try {
        const appToken =
          await getAppSessionToken();

        if (!appToken) {
          return;
        }

        const localMlToken =
          window.localStorage.getItem(
            'mercadolibre_access_token'
          );

        const localMlUserId =
          window.localStorage.getItem(
            'mercadolibre_user_id'
          );

        const verifiedSellerId =
          window.localStorage.getItem(
            'mercadolibre_verified_user_id'
          );

        /*
         * AISLAMIENTO REAL POR PERFIL
         *
         * El seller guardado en localStorage no se acepta
         * como verdadero hasta validar el access token.
         */
        if (localMlToken) {
          try {
            const linkResponse = await fetch(
              `${apiBaseUrl}/api/mercadolibre/account-link`,
              {
                method: 'POST',
                headers: {
                  'Content-Type':
                    'application/json',
                  Authorization:
                    `Bearer ${appToken}`
                },
                body: JSON.stringify({
                  accessToken:
                    localMlToken
                })
              }
            );

            const linkPayload =
              await linkResponse
                .json()
                .catch(() => ({}));

            if (
              linkResponse.ok &&
              linkPayload?.success
            ) {
              const realSellerId =
                String(
                  linkPayload?.data?.user_id || ''
                ).trim();

              if (!realSellerId) {
                console.warn(
                  'Mercado Libre no devolvió el seller real'
                );
                return;
              }

              const sellerChanged =
                String(localMlUserId || '') !==
                realSellerId;

              window.localStorage.setItem(
                'mercadolibre_user_id',
                realSellerId
              );

              window.localStorage.setItem(
                'mercadolibre_verified_user_id',
                realSellerId
              );

              window.localStorage.setItem(
                'mercadolibre_last_validation',
                new Date().toISOString()
              );

              console.log(
                '🔐 Seller REAL del perfil validado:',
                realSellerId
              );

              /*
               * Si anteriormente quedó otro seller contaminado
               * en este perfil, recargar una sola vez para que
               * todas las pantallas lean el seller correcto.
               */
              if (sellerChanged) {
                setTimeout(() => {
                  if (!stopped) {
                    window.location.reload();
                  }
                }, 100);
              }

              return;
            }

            console.warn(
              'Conexión local Mercado Libre no válida:',
              linkPayload?.message ||
                `HTTP ${linkResponse.status}`
            );

            /*
             * Un 401 significa que el access token ya no sirve.
             * Si este perfil ya tenía un seller REAL previamente
             * verificado, podremos restaurar exactamente ese.
             */
            if (linkResponse.status === 401) {
              window.localStorage.removeItem(
                'mercadolibre_access_token'
              );
            } else {
              /*
               * Ante cualquier error diferente a expiración,
               * no intentar escoger otro seller automáticamente.
               */
              return;
            }
          } catch (error) {
            console.warn(
              'Error validando seller local:',
              error?.message || error
            );

            /*
             * Un error de red no autoriza a cambiar de seller.
             */
            return;
          }
        }

        const requestedSellerId =
          String(
            verifiedSellerId || ''
          ).trim();

        if (!requestedSellerId) {
          /*
           * Perfil proveniente de la implementación vieja:
           * no sabemos con seguridad si el ID almacenado
           * pertenece al token que acaba de vencer.
           *
           * Se exige reconexión UNA SOLA VEZ.
           */
          window.localStorage.removeItem(
            'mercadolibre_access_token'
          );

          window.localStorage.removeItem(
            'mercadolibre_refresh_token'
          );

          window.localStorage.removeItem(
            'mercadolibre_user_id'
          );

          window.localStorage.removeItem(
            'mercadolibre_token_expires_at'
          );

          window.localStorage.removeItem(
            'ml_connection'
          );

          console.warn(
            'Mercado Libre requiere reconexión para verificar este perfil'
          );

          return;
        }

        /*
         * Si el access token venció, solamente se permite
         * recuperar el seller que ESTE perfil ya verificó.
         */
        window.localStorage.setItem(
          'mercadolibre_user_id',
          requestedSellerId
        );

        const response = await fetch(
          `${apiBaseUrl}/api/mercadolibre/account-session`,
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
              Authorization:
                `Bearer ${appToken}`
            },
            cache: 'no-store',
            body: JSON.stringify({
              userId: requestedSellerId
            })
          }
        );

        const payload =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          !payload?.success
        ) {
          throw new Error(
            payload?.message ||
            `HTTP ${response.status}`
          );
        }

        const data = payload?.data;

        if (!data?.connected) {
          return;
        }

        if (
          String(data?.user_id || '') !==
          requestedSellerId
        ) {
          throw new Error(
            'El servidor intentó devolver un seller diferente al de este perfil'
          );
        }

        window.localStorage.setItem(
          'mercadolibre_verified_user_id',
          requestedSellerId
        );

        const previousAccessToken =
          window.localStorage.getItem(
            'mercadolibre_access_token'
          );

        const previousConnection =
          window.localStorage.getItem(
            'ml_connection'
          );

        if (data?.access_token) {
          window.localStorage.setItem(
            'mercadolibre_access_token',
            data.access_token
          );
        }

        /*
         * El refresh token ya NO necesita vivir
         * en el navegador.
         */
        window.localStorage.removeItem(
          'mercadolibre_refresh_token'
        );

        if (data?.user_id) {
          window.localStorage.setItem(
            'mercadolibre_user_id',
            String(data.user_id)
          );
        }

        if (false && data?.expires_at) {
          const expiryMs =
            new Date(
              data.expires_at
            ).getTime();

          if (
            Number.isFinite(expiryMs)
          ) {
            window.localStorage.setItem(
              'mercadolibre_token_expires_at',
              String(expiryMs)
            );
          }
        }

        const now =
          new Date().toISOString();

        window.localStorage.setItem(
          'mercadolibre_last_validation',
          now
        );

        window.localStorage.setItem(
          'mercadolibre_last_sync',
          now
        );

        const connectionData = {
          isConnected: true,
          connectionHealth: 'good',

          lastConnected:
            data?.obtained_at ||
            now,

          tokenExpiry:
            JSON.parse(window.localStorage.getItem('ml_connection') || '{}')?.tokenExpiry ||
            null,

          userId:
            data?.user_id ||
            null
        };

        const serializedConnection =
          JSON.stringify(
            connectionData
          );

        window.localStorage.setItem(
          'ml_connection',
          serializedConnection
        );

        window.dispatchEvent(
          new CustomEvent(
            'mercadolibre-account-synced',
            {
              detail:
                connectionData
            }
          )
        );

        const changed =
          previousAccessToken !==
            data?.access_token ||
          previousConnection !==
            serializedConnection;

        /*
         * Las pantallas actuales leen localStorage
         * al montarse. Recargar una sola vez cuando
         * se recupera la conexión permite conservar
         * el código existente sin romperlo.
         */
        if (changed) {
          const marker =
            `mercadobulk_ml_bootstrap:` +
            `${user.id}:` +
            `${data?.user_id || 'unknown'}:` +
            `${data?.obtained_at || 'current'}`;

          try {
            if (
              window.sessionStorage.getItem(
                marker
              ) !== '1'
            ) {
              window.sessionStorage.setItem(
                marker,
                '1'
              );

              setTimeout(() => {
                if (!stopped) {
                  window.location.reload();
                }
              }, 150);

              return;
            }
          } catch {}
        }

        console.log(
          '🔗 Conexión Mercado Libre restaurada desde el servidor'
        );
      } catch (error) {
        console.warn(
          'No se pudo restaurar Mercado Libre:',
          error?.message || error
        );
      } finally {
        runningRef.current = false;
      }
    };

    void syncMercadoLibreAccount();

    const handleFocus = () => {
      void syncMercadoLibreAccount();
    };

    const handleVisibility = () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        void syncMercadoLibreAccount();
      }
    };

    const timer =
      window.setInterval(
        () => {
          void syncMercadoLibreAccount();
        },
        60000
      );

    window.addEventListener(
      'focus',
      handleFocus
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {
      stopped = true;

      window.clearInterval(timer);

      window.removeEventListener(
        'focus',
        handleFocus
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );
    };
  }, [user?.id]);

  return null;
};

export default MercadoLibreAccountSync;
